'use client';

import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import Banner from './components/Banner';
import Section from './components/Section';
import ProductRecommend, {
  isStyleHotProductProductRecommendEnabled,
} from './components/ProductRecommend';
import StyleCategory from './components/StyleCategory';
import StyleQuizDialog from './components/StyleQuizDialog';
import {
  shouldTrackStyleProductExposure,
  type StyleProductExposureItem,
} from './hooks/styleProductExposureDedup';
import type { StyleHotProductClientProps } from './types';

export default function StyleHotProductClientPage({
  deviceType,
  runtimeData,
  runtimeConfig,
  isPreview,
  isAppCommunicationReady,
  inAppH5,
  onBottomContentReady,
}: StyleHotProductClientProps) {
  const [styleQuizOpen, setStyleQuizOpen] = useState(false);
  const [productRecommendLoaded, setProductRecommendLoaded] = useState(false);
  const [styleJumpRequest, setStyleJumpRequest] = useState<{
    styleName: string;
    requestId: number;
  } | null>(null);
  const {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackAdClick,
    trackAdExp,
    trackTopBannerAdExposure,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackStylePopupExposure,
    trackStylePopupClick,
  } = useCampaignPageTracking(runtimeConfig);
  const hasProductRecommend = isStyleHotProductProductRecommendEnabled(
    runtimeData?.productRecommend,
  );

  const handleProductRecommendLoadComplete = useCallback(() => {
    setProductRecommendLoaded(true);
  }, []);

  useEffect(() => {
    if (isAppCommunicationReady && (!hasProductRecommend || productRecommendLoaded)) {
      onBottomContentReady?.();
    }
  }, [hasProductRecommend, isAppCommunicationReady, onBottomContentReady, productRecommendLoaded]);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  const openStyleQuiz = useCallback(() => {
    setStyleQuizOpen(true);
  }, []);

  const closeStyleQuiz = useCallback(() => {
    setStyleQuizOpen(false);
  }, []);

  const handleViewStyle = useCallback((styleName: string) => {
    setStyleJumpRequest((prev) => ({
      styleName,
      requestId: (prev?.requestId || 0) + 1,
    }));
  }, []);

  const handleMarketingAdClick = useCallback(
    (index: number, extraProperties: Record<string, string>) => {
      trackAdClick('广告位版位', index, undefined, extraProperties);
    },
    [trackAdClick],
  );

  const handleTopBannerAdClick = useCallback(
    (clickArea: '图片' | '按钮', extraProperties: Record<string, string>) => {
      trackAdClick('顶部banner位', 0, undefined, {
        click_area: clickArea,
        ...extraProperties,
      });
    },
    [trackAdClick],
  );

  const handleMarketingAdExposure = useCallback(
    (index: number, extraProperties: Record<string, string>) => {
      trackAdExp('广告位版位', '', index, extraProperties);
    },
    [trackAdExp],
  );

  const handleStyleProductExposure = useCallback(
    (item: StyleProductExposureItem, index: number, moduleName: string, tabName?: string) => {
      if (!shouldTrackStyleProductExposure(item)) {
        return;
      }

      trackProductListPitsExposure(item, index, moduleName, tabName);
    },
    [trackProductListPitsExposure],
  );

  return (
    <Box className="w-full bg-white">
      <Banner
        deviceType={deviceType}
        data={runtimeData?.banner}
        onOpenStyleQuiz={openStyleQuiz}
        onTrackAdExposure={trackTopBannerAdExposure}
        onTrackAdClick={handleTopBannerAdClick}
      />

      <StyleCategory
        deviceType={deviceType}
        data={runtimeData?.style_category}
        marketingPageId={runtimeData?.marketing_page_id}
        adp={runtimeData?.adp}
        isPreview={isPreview}
        inAppH5={inAppH5}
        styleJumpRequest={styleJumpRequest}
        onTrackNavTabClick={trackNavTabClick}
        onTrackProductClick={trackProductListPitsClick}
        onTrackProductExposure={handleStyleProductExposure}
      />
      <Section
        deviceType={deviceType}
        data={runtimeData?.marketing}
        onTrackAdClick={handleMarketingAdClick}
        onTrackAdExposure={handleMarketingAdExposure}
      />

      <ProductRecommend
        deviceType={deviceType}
        config={runtimeData?.productRecommend}
        marketingPageId={runtimeData?.marketing_page_id}
        onProductClick={trackProductListPitsClick}
        onProductExposure={handleStyleProductExposure}
        onLoadComplete={handleProductRecommendLoadComplete}
      />
      <StyleQuizDialog
        open={styleQuizOpen}
        deviceType={deviceType}
        data={runtimeData?.styleQuiz}
        onClose={closeStyleQuiz}
        onViewStyle={handleViewStyle}
        onStylePopupExposure={trackStylePopupExposure}
        onStylePopupClick={trackStylePopupClick}
      />
    </Box>
  );
}
